/**
 * CloudLedger App
 * 完整前端应用 - 使用 RPC Client 调用 API
 */

import { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { AIKeyProvider, useAIKey } from "./hooks/useAIKey";
import { TransactionForm } from "./components/transactions/TransactionForm";
import { AIChatBox } from "./components/ai/AIChatBox";
import { client, createAuthenticatedClient } from "./lib/api";

// ============================================================================
// Types
// ============================================================================

interface Transaction {
    id: string;
    amount: number;
    date: string;
    note: string | null;
    categoryId: string;
    categoryName?: string;
}

interface Ledger {
    id: string;
    name: string;
    currency: string;
}

interface Category {
    id: string;
    name: string;
    type: "income" | "expense";
}

// ============================================================================
// Login Page
// ============================================================================

function LoginPage() {
    const { login } = useAuth();
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            // 使用 RPC client 而不是 fetch
            const endpoint = isLogin ? "login" : "register";
            const res = await client.api.auth[endpoint as "login" | "register"].$post({
                json: { email, password },
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error((data as { error?: string }).error || "操作失败");
            }

            const result = data as { success: boolean; data?: { token: string } };
            if (result.data?.token) {
                login(result.data.token);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "操作失败");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-white mb-2">☁️ CloudLedger</h1>
                    <p className="text-purple-200">智能云记账</p>
                </div>

                {/* Card */}
                <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl">
                    {/* Tabs */}
                    <div className="flex mb-6 bg-white/5 rounded-lg p-1">
                        <button
                            onClick={() => setIsLogin(true)}
                            className={`flex-1 py-2 rounded-md text-sm font-medium transition ${isLogin ? "bg-white text-purple-900" : "text-white"
                                }`}
                        >
                            登录
                        </button>
                        <button
                            onClick={() => setIsLogin(false)}
                            className={`flex-1 py-2 rounded-md text-sm font-medium transition ${!isLogin ? "bg-white text-purple-900" : "text-white"
                                }`}
                        >
                            注册
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-purple-200 text-sm mb-2">邮箱</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-400"
                                placeholder="your@email.com"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-purple-200 text-sm mb-2">密码</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-400"
                                placeholder="••••••••"
                                required
                                minLength={6}
                            />
                        </div>

                        {error && (
                            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-medium rounded-lg hover:from-purple-600 hover:to-indigo-600 transition disabled:opacity-50"
                        >
                            {loading ? "请稍候..." : isLogin ? "登录" : "注册"}
                        </button>
                    </form>
                </div>

                {/* Footer */}
                <p className="text-center text-purple-300/60 text-sm mt-6">
                    基于 Cloudflare 全栈 Serverless 构建
                </p>
            </div>
        </div>
    );
}

// ============================================================================
// Dashboard
// ============================================================================

function Dashboard() {
    const { token, logout } = useAuth();
    const { apiKey, setApiKey } = useAIKey();

    const [ledgers, setLedgers] = useState<Ledger[]>([]);
    const [currentLedger, setCurrentLedger] = useState<Ledger | null>(null);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [showAIChat, setShowAIChat] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [balance, setBalance] = useState(0);
    const [loading, setLoading] = useState(true);

    // 获取认证后的 client
    const getClient = () => {
        if (!token) throw new Error("Not authenticated");
        return createAuthenticatedClient(token);
    };

    // 加载账本列表
    useEffect(() => {
        if (token) {
            loadLedgers();
            loadCategories();
        }
    }, [token]);

    // 加载交易记录
    useEffect(() => {
        if (currentLedger && token) {
            loadTransactions(currentLedger.id);
            loadBalance(currentLedger.id);
        }
    }, [currentLedger, token]);

    const loadLedgers = async () => {
        try {
            const authClient = getClient();
            const res = await authClient.api.ledgers.$get();
            const data = await res.json();

            if (res.ok) {
                const result = data as { data?: { ledgers: Ledger[] } };
                if (result.data?.ledgers) {
                    setLedgers(result.data.ledgers);
                    if (result.data.ledgers.length > 0) {
                        setCurrentLedger(result.data.ledgers[0]);
                    }
                }
            }
        } catch (err) {
            console.error("Failed to load ledgers:", err);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const authClient = getClient();
            const res = await authClient.api.categories.$get();
            const data = await res.json();

            if (res.ok) {
                const result = data as { data?: { categories: Category[] } };
                if (result.data?.categories) {
                    setCategories(result.data.categories);
                }
            }
        } catch (err) {
            console.error("Failed to load categories:", err);
        }
    };

    const loadTransactions = async (ledgerId: string) => {
        try {
            const authClient = getClient();
            const res = await authClient.api.transactions.$get({
                query: { ledgerId, limit: "50", page: "1" }
            });
            const json = await res.json();

            if (res.ok) {
                // API returns { success, data: [...transactions], pagination }
                const result = json as { success?: boolean; data?: Transaction[] };
                if (result.data && Array.isArray(result.data)) {
                    setTransactions(result.data);
                }
            }
        } catch (err) {
            console.error("Failed to load transactions:", err);
        }
    };

    const loadBalance = async (ledgerId: string) => {
        try {
            const authClient = getClient();
            const res = await authClient.api.transactions.balance[":ledgerId"].$get({
                param: { ledgerId }
            });
            const json = await res.json();

            if (res.ok) {
                // API returns { success, data: { ledgerId, balance, transactionCount } }
                const result = json as { success?: boolean; data?: { balance: number } };
                if (result.data?.balance !== undefined) {
                    setBalance(result.data.balance);
                }
            }
        } catch (err) {
            console.error("Failed to load balance:", err);
        }
    };

    const createLedger = async () => {
        const name = prompt("请输入账本名称:");
        if (!name) return;

        try {
            const authClient = getClient();
            const res = await authClient.api.ledgers.$post({
                json: { name, currency: "CNY" }
            });
            const data = await res.json();

            if (res.ok) {
                const result = data as { data?: { ledger: Ledger } };
                if (result.data?.ledger) {
                    setLedgers([...ledgers, result.data.ledger]);
                    setCurrentLedger(result.data.ledger);
                }
            }
        } catch (err) {
            alert("创建失败");
        }
    };

    const deleteTransaction = async (id: string) => {
        if (!confirm("确定删除这笔交易?")) return;

        try {
            const authClient = getClient();
            await authClient.api.transactions[":id"].$delete({
                param: { id }
            });
            setTransactions(transactions.filter(t => t.id !== id));
            if (currentLedger) loadBalance(currentLedger.id);
        } catch (err) {
            alert("删除失败");
        }
    };

    const formatAmount = (cents: number) => {
        const yuan = cents / 100;
        return yuan >= 0 ? `+¥${yuan.toFixed(2)}` : `-¥${Math.abs(yuan).toFixed(2)}`;
    };

    const getCategoryName = (categoryId: string) => {
        return categories.find(c => c.id === categoryId)?.name || "未分类";
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-100 flex items-center justify-center">
                <div className="text-slate-500">加载中...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-100">
            {/* Header */}
            <header className="bg-white shadow-sm border-b sticky top-0 z-10">
                <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <h1 className="text-xl font-bold text-slate-800">☁️ CloudLedger</h1>
                        {/* Ledger Selector */}
                        <select
                            value={currentLedger?.id || ""}
                            onChange={(e) => {
                                const ledger = ledgers.find(l => l.id === e.target.value);
                                if (ledger) setCurrentLedger(ledger);
                            }}
                            className="px-3 py-1.5 border rounded-lg text-sm"
                        >
                            {ledgers.map(l => (
                                <option key={l.id} value={l.id}>{l.name}</option>
                            ))}
                        </select>
                        <button
                            onClick={createLedger}
                            className="text-sm text-indigo-600 hover:text-indigo-800"
                        >
                            + 新账本
                        </button>
                    </div>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => setShowSettings(true)}
                            className="text-slate-500 hover:text-slate-700"
                            title="设置"
                        >
                            ⚙️
                        </button>
                        <button
                            onClick={logout}
                            className="text-sm text-slate-500 hover:text-slate-700"
                        >
                            退出
                        </button>
                    </div>
                </div>
            </header>

            {/* Main */}
            <main className="max-w-6xl mx-auto px-4 py-6">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                        <h2 className="text-sm font-medium text-slate-500 mb-1">当前余额</h2>
                        <p className={`text-3xl font-bold ${balance >= 0 ? 'text-slate-900' : 'text-red-500'}`}>
                            ¥{(balance / 100).toFixed(2)}
                        </p>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                        <h2 className="text-sm font-medium text-slate-500 mb-1">本月交易</h2>
                        <p className="text-3xl font-bold text-slate-900">{transactions.length}</p>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                        <h2 className="text-sm font-medium text-slate-500 mb-1">AI 助手</h2>
                        <button
                            onClick={() => setShowAIChat(true)}
                            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg text-sm hover:from-purple-600 hover:to-indigo-600 transition"
                        >
                            💬 开始对话
                        </button>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-slate-800">交易记录</h2>
                    <button
                        onClick={() => setShowAddForm(true)}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition"
                    >
                        + 添加交易
                    </button>
                </div>

                {/* Transactions List */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    {transactions.length === 0 ? (
                        <div className="p-12 text-center text-slate-400">
                            暂无交易记录，点击上方按钮添加
                        </div>
                    ) : (
                        <div className="divide-y">
                            {transactions.map(tx => (
                                <div key={tx.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-lg">
                                            {tx.amount >= 0 ? "📈" : "📉"}
                                        </div>
                                        <div>
                                            <p className="font-medium text-slate-800">
                                                {tx.note || getCategoryName(tx.categoryId)}
                                            </p>
                                            <p className="text-sm text-slate-500">
                                                {getCategoryName(tx.categoryId)} · {new Date(tx.date).toLocaleDateString("zh-CN")}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className={`font-semibold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                            {formatAmount(tx.amount)}
                                        </span>
                                        <button
                                            onClick={() => deleteTransaction(tx.id)}
                                            className="text-slate-400 hover:text-red-500 text-sm"
                                        >
                                            删除
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            {/* Add Transaction Modal */}
            {showAddForm && currentLedger && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">添加交易</h3>
                        <TransactionForm
                            ledgerId={currentLedger.id}
                            categories={categories}
                            onSuccess={() => {
                                setShowAddForm(false);
                                loadTransactions(currentLedger.id);
                                loadBalance(currentLedger.id);
                            }}
                            onCancel={() => setShowAddForm(false)}
                        />
                    </div>
                </div>
            )}

            {/* AI Chat Modal */}
            {showAIChat && currentLedger && (
                <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
                    <div className="bg-white rounded-t-2xl md:rounded-2xl w-full max-w-lg h-[80vh] flex flex-col">
                        <div className="p-4 border-b flex justify-between items-center">
                            <h3 className="font-semibold">AI 助手</h3>
                            <button onClick={() => setShowAIChat(false)} className="text-slate-400">✕</button>
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <AIChatBox />
                        </div>
                    </div>
                </div>
            )}

            {/* Settings Modal */}
            {showSettings && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">设置</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    OpenAI API Key (BYOK)
                                </label>
                                <input
                                    type="password"
                                    value={apiKey || ""}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    placeholder="sk-..."
                                    className="w-full px-3 py-2 border rounded-lg text-sm"
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    用于 AI 分类建议和自然语言查询
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setShowSettings(false)}
                                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm"
                            >
                                关闭
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ============================================================================
// App Root
// ============================================================================

function AppContent() {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-white">加载中...</div>
            </div>
        );
    }

    return isAuthenticated ? <Dashboard /> : <LoginPage />;
}

export default function App() {
    return (
        <AuthProvider>
            <AIKeyProvider>
                <AppContent />
            </AIKeyProvider>
        </AuthProvider>
    );
}
